import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import localforage from 'localforage';
import { useBaseUrl } from '../../utils/utils';
import Swal from 'sweetalert2';

/* ───────────────────── constants ───────────────────── */
const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';
const BLUE = '#3b82f6';
const BLUE_SOFT = 'rgba(59,130,246,0.10)';
const LABEL: React.CSSProperties = { display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 };

/* ───────────────────── types ───────────────────── */
interface Party { name: string; role: string; email: string; signed: boolean; signedAt?: string }

interface NormalizedContract {
    uid: string;
    source: 'base' | 'digital' | 'referral';
    title: string;
    type: string;
    status: string;
    startDate: string | null;
    endDate: string | null;
    createdAt: string;
    renewable: boolean;
    parties: Party[];
    partiesSummary: string;
    terms: string;
    raw: any;
}

type Tab = 'all' | 'digital' | 'referral' | 'create';

/* ───────────────────── helpers ───────────────────── */
const fmtDate = (d: string | null | undefined) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const statusColor = (s: string) => {
    const lc = (s || '').toLowerCase();
    if (['active', 'signed', 'fully_signed'].includes(lc)) return { bg: 'rgba(16,185,129,0.12)', fg: '#059669' };
    if (['pending', 'pending_signatures', 'draft'].includes(lc)) return { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' };
    if (['expired', 'terminated', 'cancelled'].includes(lc)) return { bg: 'rgba(239,68,68,0.12)', fg: '#dc2626' };
    return { bg: 'rgba(100,116,139,0.12)', fg: '#64748b' };
};

const durationLabel = (d: string) => {
    const map: Record<string, string> = { long_term: 'Long Term', medium_term: 'Medium Term', short_term: 'Short Term' };
    return map[d] || d || '—';
};

const parseParties = (termsStr: string): { content: string; parties: Party[] } => {
    try {
        const parsed = JSON.parse(termsStr);
        if (parsed && typeof parsed === 'object' && Array.isArray(parsed.parties)) {
            return { content: parsed.content || '', parties: parsed.parties };
        }
    } catch { /* not JSON, plain text */ }
    return { content: termsStr, parties: [] };
};

const signingStatus = (parties: Party[]): string => {
    if (!parties.length) return 'draft';
    const allSigned = parties.every(p => p.signed);
    const someSigned = parties.some(p => p.signed);
    if (allSigned) return 'signed';
    if (someSigned) return 'pending_signatures';
    return 'pending_signatures';
};

/* ───────────────────── component ───────────────────── */
const ContractCenter: React.FC = () => {
    const base_url = useBaseUrl();

    const [tab, setTab] = useState<Tab>('all');
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [baseContracts, setBaseContracts] = useState<any[]>([]);
    const [digitalContracts, setDigitalContracts] = useState<any[]>([]);
    const [referralContracts, setReferralContracts] = useState<any[]>([]);

    const [detail, setDetail] = useState<NormalizedContract | null>(null);

    /* create form — all contracts are digital */
    const emptyParty = (): Party => ({ name: '', role: '', email: '', signed: false });
    const [form, setForm] = useState({
        title: '',
        description: '',
        contract_type: 'service_agreement' as string,
        contract_duration: 'long_term',
        contract_term: 'term',
        renewable: false,
        terms: '',
        start_date: '',
        end_date: '',
    });
    const [formParties, setFormParties] = useState<Party[]>([emptyParty(), emptyParty()]);
    const [formBusy, setFormBusy] = useState(false);

    /* sign panel */
    const [signPanel, setSignPanel] = useState(false);
    const [signData, setSignData] = useState({ signer_type: 'clinic', signer_name: '', signer_email: '', notes: '' });
    const [signBusy, setSignBusy] = useState(false);

    /* ── data loading ── */
    useEffect(() => {
        document.title = 'Contract Center - Uzima';
        loadAll();
    }, []);

    const loadAll = async () => {
        setLoading(true);
        const clinic_id = await localforage.getItem('clinic_id');

        const fetches = await Promise.allSettled([
            axios.get(`${base_url}collaborations/contracts/`, { timeout: 12000 }).catch(() => null),
            axios.get(`${base_url}collaborations/digital-contracts/`, { timeout: 12000 }).catch(() => null),
            clinic_id
                ? axios.post(`${base_url}facilities/referrals/contracts`, { clinic_id }, { timeout: 12000 }).catch(() => null)
                : Promise.resolve(null),
        ]);

        const parse = (r: any) => {
            if (!r || r.status !== 'fulfilled' || !r.value) return [];
            const d = r.value?.data;
            if (d?.status === 'success' && Array.isArray(d.data)) return d.data;
            if (Array.isArray(d?.data)) return d.data;
            if (Array.isArray(d)) return d;
            return [];
        };

        setBaseContracts(parse(fetches[0]));
        setDigitalContracts(parse(fetches[1]));
        setReferralContracts(parse(fetches[2]));
        setLoading(false);
    };

    /* ── normalize ── */
    const normalized = useMemo<NormalizedContract[]>(() => {
        const out: NormalizedContract[] = [];

        baseContracts.forEach((c: any) => {
            const { content, parties } = parseParties(c.terms || '');
            const derivedStatus = parties.length ? signingStatus(parties)
                : (c.start_date && c.end_date ? (new Date(c.end_date) > new Date() ? 'active' : 'expired') : 'draft');
            out.push({
                uid: `ctr-${c.id}`,
                source: 'base',
                title: c.contract || `Contract #${c.id}`,
                type: (c.contract_type || 'digital').replace(/_/g, ' '),
                status: derivedStatus,
                startDate: c.start_date,
                endDate: c.end_date,
                createdAt: c.created,
                renewable: c.renewable,
                parties,
                partiesSummary: parties.length ? parties.map(p => p.name || p.role).join(', ') : '—',
                terms: content,
                raw: c,
            });
        });

        digitalContracts.forEach((c: any) => out.push({
            uid: `dig-${c.contract_id}`,
            source: 'digital',
            title: c.title || 'Digital Contract',
            type: (c.contract_type || 'digital').replace(/_/g, ' '),
            status: c.status || 'draft',
            startDate: c.created_at,
            endDate: c.expiry_date,
            createdAt: c.created_at,
            renewable: false,
            parties: [],
            partiesSummary: c.created_by || '—',
            terms: c.description || '',
            raw: c,
        }));

        referralContracts.forEach((c: any) => out.push({
            uid: `ref-${c.id}`,
            source: 'referral',
            title: c.contract_title || `Referral Contract #${c.id}`,
            type: 'referral',
            status: c.status || 'pending',
            startDate: c.start_date,
            endDate: c.end_date,
            createdAt: c.created_at,
            renewable: c.auto_renewal,
            parties: [
                { name: c.referring_institution?.name || '—', role: 'Referring', email: c.referring_institution?.email || '', signed: false },
                { name: c.receiving_institution?.name || '—', role: 'Receiving', email: c.receiving_institution?.email || '', signed: false },
            ],
            partiesSummary: `${c.referring_institution?.name || '—'} → ${c.receiving_institution?.name || '—'}`,
            terms: c.contract_terms || '',
            raw: c,
        }));

        return out.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
    }, [baseContracts, digitalContracts, referralContracts]);

    /* ── filtered list ── */
    const filtered = useMemo(() => {
        let list = normalized;
        if (tab === 'digital') list = list.filter(c => c.source === 'base' || c.source === 'digital');
        if (tab === 'referral') list = list.filter(c => c.source === 'referral');
        if (statusFilter) list = list.filter(c => c.status === statusFilter);
        if (search) {
            const lc = search.toLowerCase();
            list = list.filter(c =>
                c.title.toLowerCase().includes(lc) ||
                c.partiesSummary.toLowerCase().includes(lc) ||
                c.type.toLowerCase().includes(lc)
            );
        }
        return list;
    }, [normalized, tab, statusFilter, search]);

    /* ── stats ── */
    const stats = useMemo(() => ({
        total: normalized.length,
        active: normalized.filter(c => ['active', 'signed', 'fully_signed'].includes(c.status)).length,
        pending: normalized.filter(c => ['pending', 'pending_signatures', 'draft'].includes(c.status)).length,
        expired: normalized.filter(c => ['expired', 'terminated'].includes(c.status)).length,
    }), [normalized]);

    /* ── form: add / remove party ── */
    const addParty = () => setFormParties([...formParties, emptyParty()]);
    const removeParty = (i: number) => { if (formParties.length > 2) setFormParties(formParties.filter((_, idx) => idx !== i)); };
    const updateParty = (i: number, field: keyof Party, value: string) => {
        const copy = [...formParties];
        (copy[i] as any)[field] = value;
        setFormParties(copy);
    };

    /* ── create digital contract ── */
    const handleCreate = async () => {
        if (!form.title.trim()) { Swal.fire({ icon: 'warning', title: 'Title Required', text: 'Enter a contract title' }); return; }
        const validParties = formParties.filter(p => p.name.trim() && p.role.trim());
        if (validParties.length < 2) { Swal.fire({ icon: 'warning', title: 'Parties Required', text: 'At least 2 signing parties are required for a digital contract.' }); return; }
        if (!form.terms.trim()) { Swal.fire({ icon: 'warning', title: 'Terms Required', text: 'Contract terms must be specified.' }); return; }

        setFormBusy(true);
        try {
            const termsPayload = JSON.stringify({
                content: form.terms,
                parties: validParties.map(p => ({ ...p, signed: false })),
            });

            await axios.post(`${base_url}collaborations/contracts/`, {
                contract: form.title,
                contract_type: 'digital',
                contract_duration: form.contract_duration,
                contract_term: form.contract_term,
                renewable: form.renewable,
                terms: termsPayload,
                start_date: form.start_date || null,
                end_date: form.end_date || null,
            });
            Swal.fire({ icon: 'success', title: 'Digital Contract Created', text: `All ${validParties.length} parties will need to sign this contract.`, timer: 2400, showConfirmButton: false });
            setForm({ title: '', description: '', contract_type: 'service_agreement', contract_duration: 'long_term', contract_term: 'term', renewable: false, terms: '', start_date: '', end_date: '' });
            setFormParties([emptyParty(), emptyParty()]);
            setTab('all');
            loadAll();
        } catch (e: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || 'Failed to create contract' });
        } finally {
            setFormBusy(false);
        }
    };

    /* ── sign contract (party-based) ── */
    const handleSign = async () => {
        if (!detail) return;
        if (!signData.signer_name.trim()) { Swal.fire({ icon: 'warning', title: 'Name Required' }); return; }

        if (detail.source === 'digital' && detail.raw.contract_id) {
            setSignBusy(true);
            try {
                await axios.post(`${base_url}collaborations/digital-contracts/${detail.raw.contract_id}/sign/`, {
                    signer_type: signData.signer_type,
                    signer_name: signData.signer_name,
                    signer_email: signData.signer_email,
                    signature_data: { timestamp: new Date().toISOString(), user_agent: navigator.userAgent, notes: signData.notes },
                    signature_hash: `sig_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
                });
                Swal.fire({ icon: 'success', title: 'Contract Signed!', timer: 1800, showConfirmButton: false });
                resetSign();
                loadAll();
            } catch (e: any) {
                Swal.fire({ icon: 'error', title: 'Signing Failed', text: e?.response?.data?.message || 'Could not apply signature' });
            } finally { setSignBusy(false); }
        } else if (detail.source === 'base' && detail.parties.length > 0) {
            setSignBusy(true);
            try {
                const updated = detail.parties.map(p =>
                    p.name === signData.signer_name || p.email === signData.signer_email
                        ? { ...p, signed: true, signedAt: new Date().toISOString() }
                        : p
                );
                const newTerms = JSON.stringify({ content: detail.terms, parties: updated });
                await axios.put(`${base_url}collaborations/contracts/`, {
                    id: detail.raw.id,
                    terms: newTerms,
                });
                Swal.fire({ icon: 'success', title: 'Signature Applied!', timer: 1800, showConfirmButton: false });
                resetSign();
                setDetail(null);
                loadAll();
            } catch (e: any) {
                Swal.fire({ icon: 'error', title: 'Signing Failed', text: e?.response?.data?.message || 'Could not apply signature' });
            } finally { setSignBusy(false); }
        }
    };

    const resetSign = () => {
        setSignPanel(false);
        setSignData({ signer_type: 'clinic', signer_name: '', signer_email: '', notes: '' });
    };

    /* ───────────────────── tabs config ───────────────────── */
    const tabs: { id: Tab; label: string; icon: string; count?: number }[] = [
        { id: 'all', label: 'All Contracts', icon: 'bx bx-file', count: normalized.length },
        { id: 'digital', label: 'Digital Contracts', icon: 'bx bx-lock-alt', count: baseContracts.length + digitalContracts.length },
        { id: 'referral', label: 'Referral', icon: 'bx bx-transfer', count: referralContracts.length },
        { id: 'create', label: 'New Digital Contract', icon: 'bx bx-plus-circle' },
    ];

    const canSign = (c: NormalizedContract) => {
        if (c.source === 'digital') return ['pending_signatures', 'draft', 'pending'].includes(c.status);
        if (c.source === 'base') return c.parties.length > 0 && !c.parties.every(p => p.signed);
        return false;
    };

    /* ───────────────────── render ───────────────────── */
    return (
        <div className="sc-wrap">

            {/* ── header ── */}
            <div className="sc-header">
                <div>
                    <h1><i className="bx bx-lock-alt me-2" style={{ color: ACCENT }}></i>Contract Center</h1>
                    <p>Create, manage, and digitally sign contracts — all parties must sign</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="sc-btn sc-btn-ghost" onClick={loadAll}><i className="bx bx-refresh"></i> Refresh</button>
                    <button className="sc-btn sc-btn-primary" onClick={() => setTab('create')}><i className="bx bx-plus"></i> New Digital Contract</button>
                </div>
            </div>

            {/* ── stats ── */}
            <div className="sc-stats" style={{ marginBottom: 20 }}>
                {[
                    { label: 'Total Contracts', val: stats.total, icon: 'bx bx-file', cls: 'teal' },
                    { label: 'Active / Signed', val: stats.active, icon: 'bx bx-check-circle', cls: 'blue' },
                    { label: 'Pending Signatures', val: stats.pending, icon: 'bx bx-time-five', cls: 'amber' },
                    { label: 'Expired', val: stats.expired, icon: 'bx bx-x-circle', cls: 'violet' },
                ].map((s, i) => (
                    <div className="sc-stat" key={i}>
                        <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                        <div className="sc-stat-value">{s.val}</div>
                        <div className="sc-stat-label">{s.label}</div>
                    </div>
                ))}
            </div>

            {/* ── tabs ── */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'rgba(15,23,42,0.03)', padding: 4, borderRadius: 12, flexWrap: 'wrap' }}>
                {tabs.map(t => (
                    <button key={t.id} onClick={() => { setTab(t.id); setDetail(null); }}
                        style={{
                            padding: '8px 16px', borderRadius: 10, border: 'none',
                            background: tab === t.id ? '#fff' : 'transparent',
                            color: tab === t.id ? ACCENT : 'var(--uzima-shell-text-muted)',
                            fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                            boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <i className={t.icon}></i> {t.label}
                        {t.count !== undefined && <span style={{ fontSize: '0.65rem', background: tab === t.id ? ACCENT_SOFT : 'rgba(0,0,0,0.05)', padding: '1px 7px', borderRadius: 8 }}>{t.count}</span>}
                    </button>
                ))}
            </div>

            {/* ──────── CREATE TAB — Digital Contract ──────── */}
            {tab === 'create' && (
                <div className="sc-card" style={{ margin: 0 }}>
                    <div style={{ padding: '12px 18px', borderBottom: `2px solid ${BLUE}`, background: 'rgba(59,130,246,0.03)' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-lock-alt me-2" style={{ color: BLUE }}></i>Create Digital Contract — All Parties Must Sign</div>
                    </div>
                    <div className="sc-card-body">
                        {/* contract details */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                            <div>
                                <label style={LABEL}>Contract Title *</label>
                                <input className="sc-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Lab Partnership Agreement" />
                            </div>
                            <div>
                                <label style={LABEL}>Contract Type</label>
                                <select className="sc-input" value={form.contract_type} onChange={e => setForm({ ...form, contract_type: e.target.value })}>
                                    <option value="service_agreement">Service Agreement</option>
                                    <option value="collaboration_agreement">Collaboration Agreement</option>
                                    <option value="referral_agreement">Referral Agreement</option>
                                    <option value="consent_form">Consent Form</option>
                                    <option value="nda">Non-Disclosure Agreement</option>
                                    <option value="custom">Custom Contract</option>
                                </select>
                            </div>
                            <div>
                                <label style={LABEL}>Duration</label>
                                <select className="sc-input" value={form.contract_duration} onChange={e => setForm({ ...form, contract_duration: e.target.value })}>
                                    <option value="long_term">Long Term</option>
                                    <option value="medium_term">Medium Term</option>
                                    <option value="short_term">Short Term</option>
                                </select>
                            </div>
                            <div>
                                <label style={LABEL}>Engagement</label>
                                <select className="sc-input" value={form.contract_term} onChange={e => setForm({ ...form, contract_term: e.target.value })}>
                                    <option value="permanent">Permanent</option>
                                    <option value="term">Term Engagement</option>
                                </select>
                            </div>
                            <div>
                                <label style={LABEL}>Start / Effective Date</label>
                                <input className="sc-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
                            </div>
                            <div>
                                <label style={LABEL}>End / Expiry Date</label>
                                <input className="sc-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
                            </div>
                        </div>

                        {/* signing parties */}
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <label style={{ ...LABEL, marginBottom: 0 }}>Signing Parties * (min 2)</label>
                                <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={addParty}><i className="bx bx-plus me-1"></i>Add Party</button>
                            </div>
                            <div style={{ background: 'rgba(59,130,246,0.03)', borderRadius: 12, padding: 14 }}>
                                {formParties.map((p, i) => (
                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, marginBottom: i < formParties.length - 1 ? 10 : 0, alignItems: 'end' }}>
                                        <div>
                                            {i === 0 && <label style={{ ...LABEL, fontSize: '0.66rem' }}>Full Name *</label>}
                                            <input className="sc-input" value={p.name} onChange={e => updateParty(i, 'name', e.target.value)} placeholder="Dr. Jane Smith" />
                                        </div>
                                        <div>
                                            {i === 0 && <label style={{ ...LABEL, fontSize: '0.66rem' }}>Role *</label>}
                                            <input className="sc-input" value={p.role} onChange={e => updateParty(i, 'role', e.target.value)} placeholder="Provider / Facility / Patient" />
                                        </div>
                                        <div>
                                            {i === 0 && <label style={{ ...LABEL, fontSize: '0.66rem' }}>Email</label>}
                                            <input className="sc-input" type="email" value={p.email} onChange={e => updateParty(i, 'email', e.target.value)} placeholder="email@example.com" />
                                        </div>
                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => removeParty(i)} disabled={formParties.length <= 2}
                                            style={{ opacity: formParties.length <= 2 ? 0.3 : 1, height: 38 }}>
                                            <i className="bx bx-x"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)', marginTop: 6 }}>
                                <i className="bx bx-info-circle me-1"></i>Every party listed must digitally sign before the contract becomes active.
                            </div>
                        </div>

                        {/* terms */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={LABEL}>Terms &amp; Conditions *</label>
                            <textarea className="sc-input" rows={6} value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} placeholder="Enter the full contract terms and conditions. Be specific about obligations, deliverables, timelines, and consequences of breach..." style={{ resize: 'vertical', minHeight: 120 }} />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer' }}>
                                <input type="checkbox" checked={form.renewable} onChange={e => setForm({ ...form, renewable: e.target.checked })} />
                                <span>Auto-Renewable on Expiry</span>
                            </label>
                        </div>

                        <div style={{ background: 'rgba(59,130,246,0.06)', padding: 14, borderRadius: 10, marginBottom: 16, fontSize: '0.78rem', color: '#1e40af' }}>
                            <i className="bx bx-shield-quarter me-1"></i>
                            This creates a <b>digital contract</b>. All listed parties will be required to apply their digital signature. The contract will only become active once every party has signed. IP addresses and timestamps are recorded for legal compliance.
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className="sc-btn sc-btn-primary" onClick={handleCreate} disabled={formBusy}>
                                {formBusy ? <><i className="bx bx-loader-alt bx-spin me-1"></i>Creating...</> : <><i className="bx bx-lock-alt me-1"></i>Create Digital Contract</>}
                            </button>
                            <button className="sc-btn sc-btn-ghost" onClick={() => setTab('all')}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ──────── LIST TAB ──────── */}
            {tab !== 'create' && !detail && (
                <>
                    <div className="sc-card" style={{ margin: '0 0 14px' }}>
                        <div className="sc-card-body" style={{ padding: '12px 18px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div className="sc-search" style={{ flex: 1, marginBottom: 0, minWidth: 200 }}>
                                <i className="bx bx-search"></i>
                                <input type="text" placeholder="Search contracts..." value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                            <select className="sc-input" style={{ width: 'auto', minWidth: 140 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="signed">Signed</option>
                                <option value="pending_signatures">Pending Signatures</option>
                                <option value="draft">Draft</option>
                                <option value="expired">Expired</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="sc-card"><div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 0' }}>
                            <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '2rem', color: ACCENT }}></i>
                            <p style={{ marginTop: 12, color: 'var(--uzima-shell-text-muted)' }}>Loading contracts...</p>
                        </div></div>
                    ) : filtered.length === 0 ? (
                        <div className="sc-card"><div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 0' }}>
                            <div style={{ width: 60, height: 60, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                <i className="bx bx-lock-alt" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                            </div>
                            <p style={{ fontWeight: 700 }}>{search || statusFilter ? 'No Matching Contracts' : 'No Contracts Yet'}</p>
                            <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>
                                {search || statusFilter ? 'Try adjusting your filters.' : 'Create your first digital contract to get started.'}
                            </p>
                            {!search && !statusFilter && (
                                <button className="sc-btn sc-btn-primary" onClick={() => setTab('create')}>
                                    <i className="bx bx-plus me-1"></i> New Digital Contract
                                </button>
                            )}
                        </div></div>
                    ) : (
                        <div className="sc-card" style={{ margin: 0 }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.06)' }}>
                                            {['Contract', 'Type', 'Parties', 'Signing Status', 'Effective', 'Expiry', 'Renewable', ''].map((h, i) => (
                                                <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(c => {
                                            const sc = statusColor(c.status);
                                            const signedCount = c.parties.filter(p => p.signed).length;
                                            return (
                                                <tr key={c.uid} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)', cursor: 'pointer', transition: 'background 0.1s' }}
                                                    onClick={() => setDetail(c)}
                                                    onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(15,118,110,0.03)'; }}
                                                    onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
                                                >
                                                    <td style={{ padding: '12px 14px', fontWeight: 600 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <div style={{ width: 36, height: 36, borderRadius: 10, background: BLUE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                <i className="bx bx-lock-alt" style={{ color: BLUE, fontSize: '1rem' }}></i>
                                                            </div>
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{c.title}</div>
                                                                <div style={{ fontSize: '0.66rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                                    {c.source === 'referral' ? 'Referral' : 'Digital'} · {c.uid}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 14px', textTransform: 'capitalize' }}>{c.type}</td>
                                                    <td style={{ padding: '12px 14px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {c.parties.length > 0
                                                            ? <span>{c.parties.length} {c.parties.length === 1 ? 'party' : 'parties'}</span>
                                                            : <span style={{ color: 'var(--uzima-shell-text-muted)' }}>{c.partiesSummary}</span>
                                                        }
                                                    </td>
                                                    <td style={{ padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700, background: sc.bg, color: sc.fg }}>{c.status.replace(/_/g, ' ')}</span>
                                                            {c.parties.length > 0 && (
                                                                <span style={{ fontSize: '0.66rem', color: 'var(--uzima-shell-text-muted)' }}>{signedCount}/{c.parties.length}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>{fmtDate(c.startDate)}</td>
                                                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>{fmtDate(c.endDate)}</td>
                                                    <td style={{ padding: '12px 14px' }}>
                                                        {c.renewable ? <i className="bx bx-check-circle" style={{ color: '#059669' }}></i> : <i className="bx bx-x" style={{ color: '#9ca3af' }}></i>}
                                                    </td>
                                                    <td style={{ padding: '12px 14px' }}>
                                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={e => { e.stopPropagation(); setDetail(c); }}>
                                                            <i className="bx bx-chevron-right"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ──────── DETAIL VIEW ──────── */}
            {detail && tab !== 'create' && (
                <div style={{ display: 'grid', gridTemplateColumns: signPanel ? '1fr 360px' : '1fr', gap: 16 }}>
                    <div>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" style={{ marginBottom: 14 }} onClick={() => { setDetail(null); resetSign(); }}>
                            <i className="bx bx-arrow-back me-1"></i> Back to list
                        </button>

                        {/* header card */}
                        <div className="sc-card" style={{ margin: '0 0 16px' }}>
                            <div style={{ padding: '12px 18px', borderBottom: `2px solid ${BLUE}`, background: 'rgba(59,130,246,0.03)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                                    <i className="bx bx-lock-alt" style={{ color: BLUE, marginRight: 8 }}></i>
                                    {detail.title}
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {(() => { const sc = statusColor(detail.status); return <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700, background: sc.bg, color: sc.fg }}>{detail.status.replace(/_/g, ' ')}</span>; })()}
                                    {canSign(detail) && (
                                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => setSignPanel(!signPanel)}>
                                            <i className="bx bx-pen me-1"></i> Sign Contract
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="sc-card-body">
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
                                    {[
                                        { label: 'Type', value: detail.type },
                                        { label: 'Effective Date', value: fmtDate(detail.startDate) },
                                        { label: 'Expiry Date', value: fmtDate(detail.endDate) },
                                        { label: 'Created', value: fmtDate(detail.createdAt) },
                                        { label: 'Renewable', value: detail.renewable ? 'Yes' : 'No' },
                                        { label: 'Contract ID', value: detail.uid },
                                    ].map((item, i) => (
                                        <div key={i}>
                                            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--uzima-shell-text-muted)', marginBottom: 2 }}>{item.label}</div>
                                            <div style={{ fontSize: '0.84rem', fontWeight: 600, textTransform: 'capitalize' }}>{item.value}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* signing parties */}
                        {detail.parties.length > 0 && (
                            <div className="sc-card" style={{ margin: '0 0 16px' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-pen me-2" style={{ color: BLUE }}></i>Signing Parties ({detail.parties.filter(p => p.signed).length}/{detail.parties.length} signed)</div>
                                </div>
                                <div className="sc-card-body">
                                    {detail.parties.map((p, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: p.signed ? 'rgba(16,185,129,0.05)' : 'rgba(245,158,11,0.05)', borderRadius: 10, marginBottom: i < detail.parties.length - 1 ? 8 : 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <div style={{ width: 36, height: 36, borderRadius: 10, background: p.signed ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <i className={p.signed ? 'bx bx-check' : 'bx bx-time-five'} style={{ color: p.signed ? '#059669' : '#d97706', fontSize: '1.1rem' }}></i>
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{p.name}</div>
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{p.role}{p.email ? ` · ${p.email}` : ''}</div>
                                                </div>
                                            </div>
                                            <div>
                                                {p.signed
                                                    ? <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700, background: 'rgba(16,185,129,0.12)', color: '#059669' }}>Signed{p.signedAt ? ` · ${fmtDate(p.signedAt)}` : ''}</span>
                                                    : <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700, background: 'rgba(245,158,11,0.12)', color: '#b45309' }}>Awaiting</span>
                                                }
                                            </div>
                                        </div>
                                    ))}
                                    {detail.parties.every(p => p.signed) && (
                                        <div style={{ marginTop: 12, textAlign: 'center', padding: '10px', background: 'rgba(16,185,129,0.08)', borderRadius: 10 }}>
                                            <i className="bx bx-check-double me-1" style={{ color: '#059669' }}></i>
                                            <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#059669' }}>All parties have signed — contract is active</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* terms */}
                        <div className="sc-card" style={{ margin: '0 0 16px' }}>
                            <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-list-check me-2" style={{ color: ACCENT }}></i>Terms &amp; Conditions</div>
                            </div>
                            <div className="sc-card-body">
                                {detail.terms ? (
                                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.82rem', lineHeight: 1.7, margin: 0, fontFamily: 'inherit', background: 'rgba(15,23,42,0.02)', padding: 16, borderRadius: 10 }}>{detail.terms}</pre>
                                ) : (
                                    <p style={{ color: 'var(--uzima-shell-text-muted)', fontSize: '0.82rem' }}>No terms specified.</p>
                                )}
                            </div>
                        </div>

                        {/* referral-specific: institution parties visual */}
                        {detail.source === 'referral' && detail.raw.referring_institution && (
                            <div className="sc-card" style={{ margin: '0 0 16px' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-buildings me-2" style={{ color: '#a855f7' }}></i>Institution Parties</div>
                                </div>
                                <div className="sc-card-body">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, alignItems: 'center' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ width: 48, height: 48, borderRadius: 12, background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                                                <i className="bx bx-building-house" style={{ color: ACCENT, fontSize: '1.2rem' }}></i>
                                            </div>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{detail.raw.referring_institution?.name}</div>
                                            <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Referring</div>
                                        </div>
                                        <i className="bx bx-right-arrow-alt" style={{ fontSize: '1.5rem', color: 'var(--uzima-shell-text-muted)' }}></i>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(168,85,247,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                                                <i className="bx bx-building-house" style={{ color: '#a855f7', fontSize: '1.2rem' }}></i>
                                            </div>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{detail.raw.receiving_institution?.name}</div>
                                            <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Receiving</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* base contract extra info */}
                        {detail.source === 'base' && (
                            <div className="sc-card" style={{ margin: '0 0 16px' }}>
                                <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-detail me-2" style={{ color: ACCENT }}></i>Contract Details</div>
                                </div>
                                <div className="sc-card-body">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                                        <div>
                                            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--uzima-shell-text-muted)' }}>Duration</div>
                                            <div style={{ fontSize: '0.84rem', fontWeight: 600 }}>{durationLabel(detail.raw.contract_duration)}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--uzima-shell-text-muted)' }}>Engagement</div>
                                            <div style={{ fontSize: '0.84rem', fontWeight: 600, textTransform: 'capitalize' }}>{detail.raw.contract_term || '—'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--uzima-shell-text-muted)' }}>Attached Files</div>
                                            <div style={{ fontSize: '0.84rem', fontWeight: 600 }}>{detail.raw.contract_files?.length || 0}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── sign panel (right side) ── */}
                    {signPanel && (
                        <div>
                            <div className="sc-card" style={{ margin: 0, position: 'sticky', top: 20 }}>
                                <div style={{ padding: '12px 18px', borderBottom: `2px solid ${BLUE}`, background: 'rgba(59,130,246,0.03)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-pen me-2" style={{ color: BLUE }}></i>Sign This Contract</div>
                                </div>
                                <div className="sc-card-body">
                                    {detail.parties.length > 0 && (
                                        <div style={{ marginBottom: 14 }}>
                                            <label style={LABEL}>Select Your Identity</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                {detail.parties.filter(p => !p.signed).map((p, i) => (
                                                    <button key={i} className="sc-btn sc-btn-ghost sc-btn-sm" style={{ justifyContent: 'flex-start', textAlign: 'left', background: signData.signer_name === p.name ? BLUE_SOFT : undefined }}
                                                        onClick={() => setSignData({ ...signData, signer_name: p.name, signer_email: p.email, signer_type: p.role.toLowerCase() })}>
                                                        <i className="bx bx-user me-1"></i>{p.name} ({p.role})
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginBottom: 12 }}>
                                        <label style={LABEL}>Signer Type</label>
                                        <select className="sc-input" value={signData.signer_type} onChange={e => setSignData({ ...signData, signer_type: e.target.value })}>
                                            <option value="clinic">Clinic / Facility</option>
                                            <option value="patient">Patient</option>
                                            <option value="professional">Professional</option>
                                            <option value="provider">Provider</option>
                                        </select>
                                    </div>
                                    <div style={{ marginBottom: 12 }}>
                                        <label style={LABEL}>Full Name *</label>
                                        <input className="sc-input" value={signData.signer_name} onChange={e => setSignData({ ...signData, signer_name: e.target.value })} placeholder="Your full legal name" />
                                    </div>
                                    <div style={{ marginBottom: 12 }}>
                                        <label style={LABEL}>Email</label>
                                        <input className="sc-input" type="email" value={signData.signer_email} onChange={e => setSignData({ ...signData, signer_email: e.target.value })} placeholder="Your email" />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={LABEL}>Notes</label>
                                        <textarea className="sc-input" rows={2} value={signData.notes} onChange={e => setSignData({ ...signData, notes: e.target.value })} placeholder="Optional..." style={{ resize: 'vertical' }} />
                                    </div>
                                    <div style={{ background: 'rgba(245,158,11,0.08)', padding: 12, borderRadius: 10, marginBottom: 14, fontSize: '0.74rem', color: '#92400e' }}>
                                        <i className="bx bx-shield-quarter me-1"></i>
                                        By signing you confirm this is a legally binding digital signature. Your IP and browser information will be recorded.
                                    </div>
                                    <button className="sc-btn sc-btn-primary" style={{ width: '100%' }} onClick={handleSign} disabled={signBusy}>
                                        {signBusy ? <><i className="bx bx-loader-alt bx-spin me-1"></i>Signing...</> : <><i className="bx bx-pen me-1"></i>Apply Digital Signature</>}
                                    </button>
                                    <button className="sc-btn sc-btn-ghost" style={{ width: '100%', marginTop: 6 }} onClick={resetSign}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContractCenter;
